import { EsignFieldBox } from "@/pages/esign/components/EsignFieldBox.tsx";
import {
  fieldTypeConfig,
  fieldTypeOrder,
  findNonOverlappingPosition,
  getDefaultFilledBy,
  getRoleBoxClass,
  getSignerTitle,
  isFillableFieldType,
} from "@/pages/esign/esign.config.ts";
import {
  type PdfPageBaseLayout,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  Stack,
} from "@skailer-com/uikit";
import {
  ApiEsignMarkupField,
  ApiEsignMarkupRole,
  EsignFieldFilledBy,
  EsignFieldType,
} from "@/types/esign.types.ts";
import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

export interface EsignPageFieldEntry {
  roleIndex: number;
  fieldIndex: number;
  field: ApiEsignMarkupField;
}

interface PlacementMenu {
  xPct: number;
  yPct: number;
  baseX: number;
  baseY: number;
}

export interface EsignFieldsLayerProps {
  pageNumber: number;
  baseLayout: PdfPageBaseLayout;
  roles: ApiEsignMarkupRole[];
  fields: EsignPageFieldEntry[];
  onAddField: (roleIndex: number, field: Omit<ApiEsignMarkupField, "id">) => void;
  onUpdateField: (
    roleIndex: number,
    fieldIndex: number,
    patch: Partial<ApiEsignMarkupField>
  ) => void;
  onDeleteField: (roleIndex: number, fieldIndex: number) => void;
  onChangeFieldType: (roleIndex: number, fieldIndex: number, fieldType: EsignFieldType) => void;
  onMoveFieldToRole: (roleIndex: number, fieldIndex: number, targetRoleIndex: number) => void;
}

export function EsignFieldsLayer({
  pageNumber,
  baseLayout,
  roles,
  fields,
  onAddField,
  onUpdateField,
  onDeleteField,
  onChangeFieldType,
  onMoveFieldToRole,
}: EsignFieldsLayerProps) {
  const { t } = useTranslation();

  const layerRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);

  const [menu, setMenu] = useState<PlacementMenu | null>(null);

  const canPlace = roles.length > 0;

  const hideGhost = () => {
    if (ghostRef.current) ghostRef.current.style.opacity = "0";
  };

  const handlePointerMove = (event: ReactPointerEvent) => {
    if (!canPlace || menu || event.target !== layerRef.current) {
      hideGhost();
      return;
    }
    const layer = layerRef.current;
    if (!layer) return;

    const rect = layer.getBoundingClientRect();
    cursorRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };

    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      const ghost = ghostRef.current;
      if (!ghost) return;
      ghost.style.left = `${cursorRef.current.x}px`;
      ghost.style.top = `${cursorRef.current.y}px`;
      ghost.style.opacity = "1";
    });
  };

  const placeField = (
    activeMenu: PlacementMenu,
    roleIndex: number,
    type: EsignFieldType,
    filledBy: EsignFieldFilledBy
  ) => {
    const config = fieldTypeConfig[type];
    const page = pageNumber - 1;
    const obstacles = roles.flatMap((role) => role.fields.filter((f) => f.page === page));
    const { pos_x, pos_y } = findNonOverlappingPosition(
      obstacles,
      activeMenu.baseX - config.defaultWidth / 2,
      activeMenu.baseY - config.defaultHeight / 2,
      config.defaultWidth,
      config.defaultHeight,
      baseLayout.width,
      baseLayout.height
    );
    onAddField(roleIndex, {
      field_type: type,
      page,
      pos_x,
      pos_y,
      width: config.defaultWidth,
      height: config.defaultHeight,
      font_size: config.defaultFontSize,
      filled_by: filledBy,
      label: null,
      is_required: true,
      is_multiline: false,
    });
    setMenu(null);
  };

  const handleClick = (event: ReactMouseEvent) => {
    if (event.target !== layerRef.current) return;
    if (!canPlace) return;

    const rect = layerRef.current.getBoundingClientRect();
    const ratioX = (event.clientX - rect.left) / rect.width;
    const ratioY = (event.clientY - rect.top) / rect.height;

    hideGhost();
    setMenu({
      xPct: ratioX * 100,
      yPct: ratioY * 100,
      baseX: ratioX * baseLayout.width,
      baseY: ratioY * baseLayout.height,
    });
  };

  return (
    <div
      ref={layerRef}
      className={"absolute inset-0"}
      style={{ cursor: canPlace ? "copy" : "default" }}
      onPointerMove={handlePointerMove}
      onPointerLeave={hideGhost}
      onClick={handleClick}
    >
      <div
        ref={ghostRef}
        className={"pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2 opacity-0"}
      >
        <ButtonIcon
          variant={"primary"}
          size={"sm"}
          icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
        />
      </div>

      {fields.map((entry) => (
        <EsignFieldBox
          key={`${entry.roleIndex}-${entry.fieldIndex}`}
          field={entry.field}
          roleIndex={entry.roleIndex}
          baseWidth={baseLayout.width}
          baseHeight={baseLayout.height}
          boxClass={getRoleBoxClass(entry.roleIndex)}
          roles={roles}
          layerRef={layerRef}
          onUpdate={(patch) => {
            onUpdateField(entry.roleIndex, entry.fieldIndex, patch);
          }}
          onDelete={() => {
            onDeleteField(entry.roleIndex, entry.fieldIndex);
          }}
          onChangeType={(fieldType) => {
            onChangeFieldType(entry.roleIndex, entry.fieldIndex, fieldType);
          }}
          onMoveToRole={(targetRoleIndex) => {
            onMoveFieldToRole(entry.roleIndex, entry.fieldIndex, targetRoleIndex);
          }}
        />
      ))}

      {menu && (
        <div
          className={"absolute -translate-x-1/2 -translate-y-1/2"}
          style={{ left: `${menu.xPct}%`, top: `${menu.yPct}%` }}
        >
          <DropdownMenu
            open
            placement={"bottom-start"}
            onOpenChange={(open) => {
              if (!open) setMenu(null);
            }}
            trigger={
              <div className={"inline-flex rounded-xl bg-gray-50 p-2"}>
                <ButtonIcon
                  variant={"primary"}
                  size={"md"}
                  icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                />
              </div>
            }
          >
            {fieldTypeOrder.map((type) => (
              <DropdownMenuSub
                key={type}
                title={
                  <Stack direction="horizontal" items="center" gap="sm">
                    <FontAwesomeIcon
                      icon={fieldTypeConfig[type].icon}
                      className={"w-4 text-center"}
                    />
                    {t(fieldTypeConfig[type].labelKey)}
                  </Stack>
                }
              >
                {isFillableFieldType(type) && (
                  <DropdownMenuItem
                    title={t("core:esign_sender_fills_option")}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-paper-plane"} />}
                    onClick={() => placeField(menu, 0, type, EsignFieldFilledBy.sender)}
                  />
                )}
                {roles.map((role, index) => (
                  <DropdownMenuItem
                    key={`role-${index}`}
                    title={getSignerTitle(role, t)}
                    onClick={() =>
                      placeField(
                        menu,
                        index,
                        type,
                        isFillableFieldType(type)
                          ? EsignFieldFilledBy.signer
                          : getDefaultFilledBy(type)
                      )
                    }
                  />
                ))}
              </DropdownMenuSub>
            ))}
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
